<template>
  <v-page page-class="account-page">
    <toolbar fixed>
      <template v-slot:left>
        <span class="v-toolbar__text">{{
          $t("pages.account.accountTitle")
        }}</span>
      </template>
    </toolbar>

    <template v-if="isAuthenticated">
      <subscriber-avatar-editor class="mx-auto mt-4 mb-2" :user="currentUser" />

      <div class="text-center mb-3">
        <h2 class="mb-0">{{ currentUser.name }}</h2>
        <p class="text-grey-70 text-small mt-1 d-block">
          {{ currentUser.email }}
        </p>
      </div>

      <div class="container mb-2 px-2">
        <add-to-homescreen context="account" />
      </div>

      <list
        v-if="memberToMemberMessagingEnabled && !canMessageAll"
        boxed
        :heading="$t('pages.account.settingsHeading')"
        class="mb-2"
      >
        <list-item
          @click="
            () => $router.push(addToHash($route.hash, MESSAGE_SETTING_HASH))
          "
        >
          <template slot="left">
            <v-icon icon="compare_arrows" />
          </template>
          <template slot="right">
            <v-icon
              :icon="canReceiveMessagesFromMembers ? 'check_circle' : 'cancel'"
            />
          </template>
          <div class="v-list-item__title --centered ml-1">
            {{ $t("pages.account.membersCanMessageMe") }}
          </div>
        </list-item>
      </list>

      <list boxed :heading="$t('pages.account.accountTitle')">
        <list-item chevron @click="() => $router.push('/me/account/details')">
          <template slot="left">
            <v-icon icon="account_circle" />
          </template>
          <div class="v-list-item__title --centered ml-1">
            {{ $t("pages.account.accountDetails") }}
          </div>
        </list-item>
        <list-item chevron @click="() => $router.push('/me/account/purchases')">
          <template slot="left">
            <v-icon icon="attach_money" />
          </template>
          <div class="v-list-item__title --centered ml-1">
            {{ $t("pages.account.purchaseHistory") }}
          </div>
        </list-item>
        <list-item
          chevron
          @click="() => $router.push('/me/account/notifications')"
        >
          <template slot="left">
            <v-icon icon="notifications_none" />
          </template>
          <div class="v-list-item__title --centered ml-1">
            {{ $t("pages.account.notificationsHeading") }}
          </div>
        </list-item>
        <list-item
          chevron
          @click="() => $router.push('/me/account/subscriptions')"
        >
          <template slot="left">
            <v-icon icon="autorenew" />
          </template>
          <div class="v-list-item__title --centered ml-1">
            {{ $t("pages.account.subscriptionsHeading") }}
          </div>
        </list-item>
      </list>

      <list class="mt-2" boxed :heading="$t('pages.account.supportHeading')">
        <list-item chevron @click="() => $router.push('/me/account/support')">
          <template slot="left">
            <v-icon icon="help_outline" />
          </template>
          <div class="v-list-item__title --centered ml-1">
            {{ $t("pages.account.getHelp") }}
          </div>
        </list-item>
        <list-item chevron @click="() => $router.push('/me/account/privacy')">
          <template slot="left">
            <v-icon icon="security" />
          </template>
          <div class="v-list-item__title --centered ml-1">
            {{ $t("pages.account.privacyAndTerms") }}
          </div>
        </list-item>
      </list>

      <div class="my-5">
        <v-button
          class="mt-2"
          data-cy="logout"
          ghost
          secondary
          block
          :is-loading="isLoggingOut"
          @click="logoutUser"
        >
          {{ $t("pages.account.logout") }}
        </v-button>
      </div>
    </template>

    <v-made-with-movement v-if="!isDesktop" />

    <v-modal short @close="closeModal">
      <template :slot="MESSAGE_SETTING_HASH">
        <div class="px-2 py-3">
          <label class="mb-2 d-block text-large font-weight-semi-bold">
            {{ $t("pages.account.membersCanMessageMe") }}
          </label>

          <v-radio
            :value="true"
            :current-value="this.vReceiveMessagesFromMembers"
            @change="() => (this.vReceiveMessagesFromMembers = true)"
          >
            <label>{{ $t("pages.account.yes") }}</label>
          </v-radio>

          <v-radio
            :value="false"
            :current-value="this.vReceiveMessagesFromMembers"
            @change="() => (this.vReceiveMessagesFromMembers = false)"
          >
            <label>{{ $t("pages.account.no") }}</label>
          </v-radio>

          <cta-row white-bg overlay-modal row>
            <v-button
              primary
              block
              type="submit"
              :is-loading="isLoading"
              @click="saveMessageSetting"
            >
              {{ $t("pages.account.update") }}
            </v-button>
          </cta-row>
        </div>
      </template>
    </v-modal>
  </v-page>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex"
import { addToHash, removeFromHash } from "@/lib/url-helpers"

import Toolbar from "@/components/Toolbar"
import List from "@/components/List"
import ListItem from "@/components/ListItem"
import AddToHomescreen from "@/components/AddToHomescreen"
import CtaRow from "@/components/CtaRow"
import vRadio from "@/components/forms/vRadio.vue"

const MESSAGE_SETTING_HASH = "#message-setting"

export default {
  components: {
    Toolbar,
    AddToHomescreen,
    List,
    ListItem,
    CtaRow,
    vRadio,
    SubscriberAvatarEditor: () => import("@/components/SubscriberAvatarEditor")
  },

  data() {
    return {
      isLoading: false,
      isLoggingOut: false,
      vReceiveMessagesFromMembers: false,
      MESSAGE_SETTING_HASH
    }
  },

  computed: {
    ...mapState("responsive", ["isDesktop"]),
    ...mapGetters(["memberToMemberMessagingEnabled"]),
    ...mapGetters("auth", ["currentUser", "isAuthenticated", "canMessageAll"]),
    ...mapGetters("pwa", ["canPrompt"]),

    canReceiveMessagesFromMembers() {
      return this.currentUser
        ? !!this.currentUser?.canReceiveMessagesFromMembers
        : false
    }
  },

  watch: {
    canReceiveMessagesFromMembers: {
      immediate: true,
      handler: function() {
        this.syncMessageSetting()
      }
    }
  },

  methods: {
    addToHash,

    ...mapActions("auth", ["updateSubscriber", "logout"]),

    saveMessageSetting() {
      this.isLoading = true

      this.updateSubscriber({
        canReceiveMessagesFromMembers: this.vReceiveMessagesFromMembers
      })
        .then(() => {
          this.$notify(this.$t("pages.account.detail.updateSuccess"))
          this.closeModal()
        })
        .catch(err => {
          this.$notify(err?.message || this.$t("pages.account.updateFailed"))
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    syncMessageSetting() {
      this.vReceiveMessagesFromMembers = this.canReceiveMessagesFromMembers
    },

    closeModal() {
      this.syncMessageSetting()
      this.$router.push(removeFromHash(this.$route.hash, MESSAGE_SETTING_HASH))
    },

    logoutUser() {
      this.isLoggingOut = true
      this.logout()
        .then(() => {
          this.$router.goHome()
        })
        .finally(() => {
          this.isLoggingOut = false
        })
    }
  }
}
</script>
